import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

const initialState = {
  status: "idle",
  error: null,
  courseIdQuestList: [],
};

const { httpRequest } = Http();

/**
 * 🔹 List quests by zone/course
 */
export const zoneQuestList = createAsyncThunk(
  "listZoneQuest",
  async ({ zoneId, navigate, setLoader }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/courses/quests/${zoneId}`,
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.listZoneQuest?.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * 🔹 Add Quest
 */
export const addQuest = createAsyncThunk(
  "createQuest",
  async ({ submissionData, navigate, setLoader }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: submissionData,
          url: "admin/courses/quests",
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createQuest?.error, {
        cause: err.cause,
      });
    }
  },
);

/**
 * 🔹 Edit Quest
 */
export const editQuest = createAsyncThunk(
  "updateQuest",
  async ({ submissionData, navigate, setLoader }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: submissionData,
          url: "admin/courses/quests",
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.updateQuest?.error, {
        cause: err.cause,
      });
    }
  },
);

export const saveCourseFixtures = createAsyncThunk(
  "saveCourseFixtures",
  async ({ payload, navigate, setLoader }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: payload,
          url: "admin/courses/fixtures",
        },
        navigate,
      );
    } catch (err) {
      return rejectWithValue(err.message || "Failed to save fixtures");
    }
  },
);

const questSlice = createSlice({
  name: "quests",
  initialState,
  reducers: {
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, QuestState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, QuestState);
    },

    removeQuestDataByID: (state, action) => {
      state.courseIdQuestList = state.courseIdQuestList.filter(
        (quest) => quest.id !== action.payload,
      );
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            addQuest.pending,
            editQuest.pending,
            zoneQuestList.pending,
            saveCourseFixtures.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        },
      )

      .addMatcher(
        (action) =>
          [
            addQuest.fulfilled,
            editQuest.fulfilled,
            zoneQuestList.fulfilled,
            saveCourseFixtures.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "succeeded";
          action.meta.arg.setLoader(false);

          if (action.type.startsWith("listZoneQuest")) {
            state.courseIdQuestList = action.payload.courseQuests;
          }

          if (
            ["createQuest/fulfilled", "updateQuest/fulfilled"].includes(
              action.type,
            )
          ) {
            questSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]]?.success,
              },
            });
          }
        },
      )

      .addMatcher(
        (action) =>
          [
            addQuest.rejected,
            editQuest.rejected,
            zoneQuestList.rejected,
            saveCourseFixtures.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          action.meta.arg.setLoader(false);

          questSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        },
      );
  },
});

export const { removeQuestDataByID } = questSlice.actions;

export default questSlice.reducer;
